import { adminSidebar } from '@/config/sidebarConfig';
import { getAdminStatsApi } from '@/lib/api/usersApi.ts';
import { PaymentStatus, StripeAccountStatus, SubPlan, SubscriptionStatus } from '@shared/enums/domain.enum';
import type { AdminStatsRecentOrg, AdminStatsRecentPayment } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CheckCircle,
  ClipboardList,
  Link2,
  RefreshCw,
  Shield,
  TrendingUp,
  Trophy,
  Users,
  XCircle,
  Zap,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';

// ─── Helpers ─────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

function monthLabel(monthStr: string) {
  const month = parseInt(monthStr.split('-')[1], 10) - 1;
  return MONTH_NAMES[month] ?? '';
}

function formatCents(cents: number): string {
  if (cents === 0) return '0 €';
  return `${(cents / 100).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
}

function pct(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

// ─── Sub-components ──────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className='rounded-xl bg-white border border-gray-100 p-5 animate-pulse'>
      <div className='flex items-center justify-between mb-4'>
        <div className='h-4 w-24 bg-gray-200 rounded' />
        <div className='h-9 w-9 bg-gray-200 rounded-lg' />
      </div>
      <div className='h-8 w-20 bg-gray-200 rounded mb-2' />
      <div className='h-3 w-32 bg-gray-100 rounded' />
    </div>
  );
}

const SKELETON_BAR_HEIGHTS = [40, 65, 30, 75, 50, 80, 35, 60, 45, 70, 55, 40];

function SkeletonBar() {
  return (
    <div className='flex items-end gap-1 h-24 animate-pulse'>
      {SKELETON_BAR_HEIGHTS.map((h, i) => (
        <div key={i} className='flex-1 bg-gray-200 rounded-sm' style={{ height: `${h}%` }} />
      ))}
    </div>
  );
}

// ─── Status badges ────────────────────────────────────────────────

function SubPlanBadge({ plan }: { plan: SubPlan }) {
  return plan === SubPlan.FORFAIT ? (
    <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700'>
      <Zap className='w-3 h-3' />
      Forfait
    </span>
  ) : (
    <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600'>
      Commission
    </span>
  );
}

function SubscriptionBadge({ status }: { status: SubscriptionStatus }) {
  const config: Record<SubscriptionStatus, { label: string; cls: string }> = {
    [SubscriptionStatus.ACTIVE]: { label: 'Actif', cls: 'bg-emerald-100 text-emerald-700' },
    [SubscriptionStatus.PAST_DUE]: { label: 'Impayé', cls: 'bg-amber-100 text-amber-700' },
    [SubscriptionStatus.CANCELED]: { label: 'Annulé', cls: 'bg-red-100 text-red-700' },
    [SubscriptionStatus.UNPAID]: { label: 'Impayé', cls: 'bg-red-100 text-red-700' },
  };
  const { label, cls } = config[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
}

function ConnectBadge({ status }: { status: StripeAccountStatus }) {
  if (status === StripeAccountStatus.ACTIVE)
    return <CheckCircle className='w-4 h-4 text-emerald-500' aria-label='Connect actif' />;
  if (status === StripeAccountStatus.RESTRICTED || status === StripeAccountStatus.DISABLED)
    return <XCircle className='w-4 h-4 text-red-500' aria-label='Connect restreint' />;
  return <AlertTriangle className='w-4 h-4 text-amber-400' aria-label='Connect en cours' />;
}

function PaymentStatusBadge({ status }: { status: PaymentStatus }) {
  const config: Record<PaymentStatus, { label: string; cls: string }> = {
    [PaymentStatus.COMPLETED]: { label: 'Payé', cls: 'bg-emerald-100 text-emerald-700' },
    [PaymentStatus.PENDING]: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
    [PaymentStatus.FAILED]: { label: 'Échoué', cls: 'bg-red-100 text-red-700' },
    [PaymentStatus.REFUNDED]: { label: 'Remboursé', cls: 'bg-slate-100 text-slate-600' },
    [PaymentStatus.PARTIALLY_REFUNDED]: { label: 'Remb. partiel', cls: 'bg-fuchsia-100 text-fuchsia-700' },
  };
  const { label, cls } = config[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
}

// ─── KPI Card ──────────────────────────────────────────────────────

interface KpiCardProps {
  title: string;
  value: string | number;
  sub: string;
  icon: React.ReactNode;
  iconBg: string;
  badge?: string;
  badgeCls?: string;
}

function KpiCard({ title, value, sub, icon, iconBg, badge, badgeCls }: KpiCardProps) {
  return (
    <div className='rounded-xl bg-white border border-gray-100 p-5 flex flex-col gap-3 shadow-sm'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium text-gray-500'>{title}</span>
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${iconBg}`}>{icon}</div>
      </div>
      <div>
        <p className='text-2xl font-bold text-gray-900'>{value}</p>
        <div className='flex items-center gap-2 mt-1'>
          <p className='text-xs text-gray-400'>{sub}</p>
          {badge && <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${badgeCls}`}>{badge}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Bar chart ────────────────────────────────────────────────────

interface BarChartProps {
  data: Array<{ month: string; value: number }>;
  color: string;
  label: string;
  formatValue?: (v: number) => string;
}

function BarChart({ data, color, label, formatValue }: BarChartProps) {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  return (
    <div>
      <div className='flex items-end gap-1 h-28'>
        {data.map(d => (
          <div key={d.month} className='flex-1 flex flex-col items-center gap-0.5 group relative'>
            <div
              className={`w-full rounded-t-sm ${color} transition-all`}
              style={{ height: d.value > 0 ? `${Math.round((d.value / maxVal) * 100)}%` : '3px' }}
            />
            {/* Tooltip */}
            <div className='absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:flex items-center whitespace-nowrap bg-gray-900 text-white text-xs rounded px-2 py-1 shadow-lg pointer-events-none z-10'>
              {formatValue ? formatValue(d.value) : d.value}
            </div>
          </div>
        ))}
      </div>
      <div className='flex gap-1 mt-1'>
        {data.map(d => (
          <div key={d.month} className='flex-1 text-center text-[9px] text-gray-400'>
            {monthLabel(d.month)}
          </div>
        ))}
      </div>
      <p className='text-xs text-gray-400 mt-1'>{label}</p>
    </div>
  );
}

// ─── Recent payment row ───────────────────────────────────────────

function PaymentRow({ p }: { p: AdminStatsRecentPayment }) {
  const date = p.paidAt ? new Date(p.paidAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—';
  return (
    <div className='flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0'>
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-medium text-gray-800 truncate'>{p.competition.name}</p>
        <p className='text-xs text-gray-400 truncate'>
          {p.organization.name} · {date}
        </p>
      </div>
      <div className='text-right shrink-0'>
        <p className='text-sm font-semibold text-gray-800'>{formatCents(p.amountCents)}</p>
        {p.applicationFeeCents != null && (
          <p className='text-xs text-emerald-600 font-medium'>+{formatCents(p.applicationFeeCents)} comm.</p>
        )}
      </div>
      <PaymentStatusBadge status={p.status} />
    </div>
  );
}

// ─── Recent org row ────────────────────────────────────────────────

function OrgRow({ o }: { o: AdminStatsRecentOrg }) {
  const date = new Date(o.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
  return (
    <div className='flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0'>
      <div className='h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center shrink-0'>
        <Building2 className='w-4 h-4 text-purple-600' />
      </div>
      <div className='flex-1 min-w-0'>
        <p className='text-sm font-medium text-gray-800 truncate'>{o.name}</p>
        <p className='text-xs text-gray-400'>{date}</p>
      </div>
      <div className='flex items-center gap-1.5 shrink-0'>
        <ConnectBadge status={o.stripeAccountStatus} />
        <SubscriptionBadge status={o.subscriptionStatus} />
        <SubPlanBadge plan={o.plan} />
      </div>
    </div>
  );
}

// ─── Progress bar row ─────────────────────────────────────────────

function ProgressRow({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const w = pct(value, total);
  return (
    <div>
      <div className='flex justify-between text-xs mb-1'>
        <span className='text-gray-600'>{label}</span>
        <span className='font-semibold text-gray-800'>
          {value.toLocaleString('fr-FR')} ({w}%)
        </span>
      </div>
      <div className='h-1.5 bg-gray-100 rounded-full overflow-hidden'>
        <div className={`h-full rounded-full ${color}`} style={{ width: `${w}%` }} />
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────

export default function AdminStats() {
  const {
    data: stats,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: getAdminStatsApi,
    staleTime: 5 * 60 * 1000,
  });

  const alertCount =
    (stats?.users.disabled ?? 0) +
    (stats?.organizations.bySubscriptionStatus.PAST_DUE ?? 0) +
    (stats?.organizations.bySubscriptionStatus.CANCELED ?? 0);

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8 space-y-6'>
        <PageHeader
          title='Statistiques'
          subtitle='Tableau de bord SaaS BoulesConnect'
          actions={
            <button
              onClick={() => void refetch()}
              disabled={isFetching}
              className='flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition disabled:opacity-50'
            >
              <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
              Rafraîchir
            </button>
          }
        />

        {/* ── KPI Cards — row 1 ─────────────────────────────── */}
        <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          ) : stats ? (
            <>
              <KpiCard
                title='Utilisateurs'
                value={stats.users.total.toLocaleString('fr-FR')}
                sub={`+${stats.users.newThisMonth} ce mois · ${stats.users.verified} vérifiés`}
                icon={<Users className='w-5 h-5 text-blue-600' />}
                iconBg='bg-blue-50'
                badge={`+${stats.users.newThisYear} cette année`}
                badgeCls='bg-blue-50 text-blue-700'
              />
              <KpiCard
                title='Organisateurs'
                value={stats.organizations.total.toLocaleString('fr-FR')}
                sub={`+${stats.organizations.newThisMonth} ce mois · ${stats.organizations.connectActive} Connect actifs`}
                icon={<Building2 className='w-5 h-5 text-purple-600' />}
                iconBg='bg-purple-50'
                badge={`${stats.organizations.byPlan.FORFAIT} Forfait`}
                badgeCls='bg-indigo-50 text-indigo-700'
              />
              <KpiCard
                title='Clubs'
                value={stats.clubs.total.toLocaleString('fr-FR')}
                sub={`+${stats.clubs.newThisMonth} ce mois · ${stats.clubs.active} actifs`}
                icon={<Shield className='w-5 h-5 text-emerald-600' />}
                iconBg='bg-emerald-50'
                badge={`${stats.clubs.byPlan.PRO} Pro`}
                badgeCls='bg-emerald-50 text-emerald-700'
              />
              <KpiCard
                title='Concours'
                value={stats.competitions.total.toLocaleString('fr-FR')}
                sub={`${stats.competitions.thisYear} cette année · ${stats.competitions.upcoming} à venir`}
                icon={<Trophy className='w-5 h-5 text-amber-600' />}
                iconBg='bg-amber-50'
                badge={`${stats.competitions.finished} terminés`}
                badgeCls='bg-amber-50 text-amber-700'
              />
              <KpiCard
                title='Inscriptions'
                value={stats.registrations.total.toLocaleString('fr-FR')}
                sub={`+${stats.registrations.thisMonth} ce mois · ${stats.registrations.confirmed} confirmées`}
                icon={<ClipboardList className='w-5 h-5 text-orange-600' />}
                iconBg='bg-orange-50'
                badge={`+${stats.registrations.thisYear} cette année`}
                badgeCls='bg-orange-50 text-orange-700'
              />
              <KpiCard
                title='Revenus BoulesConnect'
                value={formatCents(stats.revenue.totalFeeCents)}
                sub={`${formatCents(stats.revenue.thisYearFeeCents)} cette année · ${formatCents(stats.revenue.thisMonthFeeCents)} ce mois`}
                icon={<TrendingUp className='w-5 h-5 text-rose-600' />}
                iconBg='bg-rose-50'
                badge={`Vol. ${formatCents(stats.revenue.totalVolumeCents)}`}
                badgeCls='bg-rose-50 text-rose-700'
              />
            </>
          ) : null}
        </div>

        {/* ── Section 2 : Acquisition + Santé plateforme ────── */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
          {/* Acquisition mensuelle */}
          <div className='lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5'>
            <h3 className='font-semibold text-gray-800 mb-4'>Acquisition mensuelle — utilisateurs</h3>
            {isLoading || !stats ? (
              <SkeletonBar />
            ) : (
              <BarChart
                data={stats.users.monthly.map(m => ({ month: m.month, value: m.count }))}
                color='bg-blue-400'
                label='Nouveaux utilisateurs par mois (12 derniers mois)'
              />
            )}
          </div>

          {/* Santé plateforme */}
          <div className='bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-5'>
            <h3 className='font-semibold text-gray-800'>Santé plateforme</h3>

            {isLoading || !stats ? (
              <div className='space-y-3 animate-pulse'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='h-4 bg-gray-200 rounded' />
                ))}
              </div>
            ) : (
              <>
                {/* Plans orgs */}
                <div className='space-y-2'>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>Plans organisateurs</p>
                  <ProgressRow
                    label='Forfait (80€/mois)'
                    value={stats.organizations.byPlan.FORFAIT}
                    total={stats.organizations.total}
                    color='bg-indigo-500'
                  />
                  <ProgressRow
                    label='Commission'
                    value={stats.organizations.byPlan.COMMISSION}
                    total={stats.organizations.total}
                    color='bg-slate-400'
                  />
                </div>

                {/* Stripe Connect */}
                <div className='space-y-2'>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1'>
                    <Link2 className='w-3 h-3' />
                    Stripe Connect
                  </p>
                  <ProgressRow
                    label='Connect configuré'
                    value={stats.organizations.withConnect}
                    total={stats.organizations.total}
                    color='bg-violet-400'
                  />
                  <ProgressRow
                    label='Connect actif'
                    value={stats.organizations.connectActive}
                    total={stats.organizations.total}
                    color='bg-emerald-500'
                  />
                </div>

                {/* Abonnements */}
                <div className='space-y-2'>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>Abonnements</p>
                  <ProgressRow
                    label='Actifs'
                    value={stats.organizations.bySubscriptionStatus.ACTIVE}
                    total={stats.organizations.total}
                    color='bg-emerald-500'
                  />
                </div>

                {/* Alertes */}
                {alertCount > 0 && (
                  <div className='rounded-lg bg-red-50 border border-red-100 p-3 space-y-1'>
                    <p className='text-xs font-semibold text-red-700 flex items-center gap-1'>
                      <AlertTriangle className='w-3.5 h-3.5' />
                      Alertes ({alertCount})
                    </p>
                    {stats.users.disabled > 0 && (
                      <p className='text-xs text-red-600'>{stats.users.disabled} compte(s) désactivé(s)</p>
                    )}
                    {stats.organizations.bySubscriptionStatus.PAST_DUE > 0 && (
                      <p className='text-xs text-red-600'>
                        {stats.organizations.bySubscriptionStatus.PAST_DUE} orga(s) en impayé
                      </p>
                    )}
                    {stats.organizations.bySubscriptionStatus.CANCELED > 0 && (
                      <p className='text-xs text-red-600'>
                        {stats.organizations.bySubscriptionStatus.CANCELED} orga(s) annulé(s)
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Section 3 : Revenus + Paiements récents ─────────── */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
          {/* Revenus mensuels */}
          <div className='lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5'>
            <h3 className='font-semibold text-gray-800 mb-4'>Commissions mensuelles BoulesConnect</h3>
            {isLoading || !stats ? (
              <SkeletonBar />
            ) : (
              <BarChart
                data={stats.revenue.monthly.map(m => ({ month: m.month, value: m.feeCents }))}
                color='bg-emerald-400'
                label='Commissions encaissées par mois (12 derniers mois)'
                formatValue={formatCents}
              />
            )}
            {stats && (
              <div className='mt-4 grid grid-cols-3 gap-3 pt-3 border-t border-gray-50'>
                <div>
                  <p className='text-xs text-gray-400'>Volume total</p>
                  <p className='text-sm font-bold text-gray-800'>{formatCents(stats.revenue.totalVolumeCents)}</p>
                </div>
                <div>
                  <p className='text-xs text-gray-400'>Paiements complétés</p>
                  <p className='text-sm font-bold text-gray-800'>{stats.revenue.totalPaidPayments}</p>
                </div>
                <div>
                  <p className='text-xs text-gray-400'>Comm. cette année</p>
                  <p className='text-sm font-bold text-emerald-700'>{formatCents(stats.revenue.thisYearFeeCents)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Paiements récents */}
          <div className='bg-white rounded-xl border border-gray-100 shadow-sm p-5'>
            <h3 className='font-semibold text-gray-800 mb-3'>Paiements récents</h3>
            {isLoading || !stats ? (
              <div className='space-y-3 animate-pulse'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='h-10 bg-gray-100 rounded' />
                ))}
              </div>
            ) : stats.recentPayments.length === 0 ? (
              <p className='text-sm text-gray-400 py-4 text-center'>Aucun paiement pour l&apos;instant</p>
            ) : (
              <div>
                {stats.recentPayments.map(p => (
                  <PaymentRow key={p.id} p={p} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Section 4 : Répartition utilisateurs + Nouvelles orgas ─ */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
          {/* Répartition utilisateurs */}
          <div className='bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-5'>
            <h3 className='font-semibold text-gray-800'>Répartition des utilisateurs</h3>

            {isLoading || !stats ? (
              <div className='space-y-3 animate-pulse'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className='h-5 bg-gray-100 rounded' />
                ))}
              </div>
            ) : (
              <>
                {/* Par rôle */}
                <div className='space-y-2'>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>Par rôle</p>
                  <ProgressRow
                    label='Joueurs (MEMBER)'
                    value={stats.users.byRole.MEMBER}
                    total={stats.users.total}
                    color='bg-blue-400'
                  />
                  <ProgressRow
                    label='Admins club'
                    value={stats.users.byRole.CLUB_ADMIN}
                    total={stats.users.total}
                    color='bg-emerald-400'
                  />
                  <ProgressRow
                    label='Organisateurs'
                    value={stats.users.byRole.ORGANIZATION_ADMIN}
                    total={stats.users.total}
                    color='bg-purple-400'
                  />
                </div>

                {/* Par plan */}
                <div className='space-y-2'>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>Par plan joueur</p>
                  <ProgressRow
                    label='Gratuit (FREE)'
                    value={stats.users.byPlan.FREE}
                    total={stats.users.total}
                    color='bg-slate-300'
                  />
                  <ProgressRow
                    label='Plus (1€/mois)'
                    value={stats.users.byPlan.PLUS}
                    total={stats.users.total}
                    color='bg-violet-500'
                  />
                </div>

                {/* Activité */}
                <div className='grid grid-cols-2 gap-3 pt-3 border-t border-gray-50'>
                  <div className='rounded-lg bg-blue-50 p-3'>
                    <p className='text-xs text-blue-500 mb-0.5'>Actifs ce mois</p>
                    <p className='text-xl font-bold text-blue-700'>
                      {stats.users.activeThisMonth.toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <div className='rounded-lg bg-red-50 p-3'>
                    <p className='text-xs text-red-400 mb-0.5'>Désactivés</p>
                    <p className='text-xl font-bold text-red-600'>{stats.users.disabled.toLocaleString('fr-FR')}</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Nouvelles organisations */}
          <div className='bg-white rounded-xl border border-gray-100 shadow-sm p-5'>
            <h3 className='font-semibold text-gray-800 mb-3'>Dernières organisations inscrites</h3>

            {isLoading || !stats ? (
              <div className='space-y-3 animate-pulse'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='h-12 bg-gray-100 rounded' />
                ))}
              </div>
            ) : stats.recentOrganizations.length === 0 ? (
              <p className='text-sm text-gray-400 py-4 text-center'>Aucune organisation</p>
            ) : (
              <div>
                {stats.recentOrganizations.map(o => (
                  <OrgRow key={o.id} o={o} />
                ))}
              </div>
            )}

            {/* Résumé concours */}
            {stats && (
              <div className='mt-4 pt-3 border-t border-gray-50 grid grid-cols-2 gap-3'>
                <div className='rounded-lg bg-amber-50 p-3'>
                  <p className='text-xs text-amber-600 mb-0.5'>Concours cette année</p>
                  <p className='text-xl font-bold text-amber-700'>
                    {stats.competitions.thisYear.toLocaleString('fr-FR')}
                  </p>
                </div>
                <div className='rounded-lg bg-orange-50 p-3'>
                  <p className='text-xs text-orange-500 mb-0.5'>Inscriptions ce mois</p>
                  <p className='text-xl font-bold text-orange-600'>
                    {stats.registrations.thisMonth.toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
